import { renderIcon } from "Components/odep-e-icon/odep-e-icon.template";
import { renderCssClasses } from "Helper/renderHelper/renderCssClasses";
import { html } from "lit-html";
import { ifDefined } from "lit-html/directives/if-defined";

import { ODEPLazyImage } from "./odep-e-lazy-image";
import lazyImageShadowScss from "./odep-e-lazy-image.shadow.scss";
import { RenderLazyImageArgs } from "./odep-e-lazy-image.types";

export interface LazyImageTemplateArgs {
  src: string;
  srcset?: string;
  sizes?: string;
  alt?: string;
  aspectRatio?: string;
  component: ODEPLazyImage;
}

const shouldRenderErrorPlaceholder = (args: LazyImageTemplateArgs) => {
  return !(args.component.hasError === false || args.component.loadingPlaceholderLoaded === true);
};

const renderErrorPlaceholder = (args: LazyImageTemplateArgs, forceRender = false) => {
  if (forceRender === false && !shouldRenderErrorPlaceholder(args)) return "";

  return html`
    <div class="odep-e-lazy-image__error-placeholder-box">
      ${renderIcon({
        iconId: "odep-icon-placeholder",
        cssClasses: "odep-e-lazy-image__error-placeholder",
      })}
    </div>
  `;
};

const renderPlaceholder = (args: LazyImageTemplateArgs) => {
  if (args.component.loadingPlaceholder === "" || args.component.loadingPlaceholderLoadingError === true) {
    return renderErrorPlaceholder(args, true);
  }

  return html`
    <img
      draggable="false"
      class="odep-e-lazy-image__placeholder"
      alt="${ifDefined(args.alt)}"
      src="${args.component.loadingPlaceholder}"
      @load=${() => {
        args.component.loadingPlaceholderLoaded = true;
        args.component.loadingPlaceholderLoadingError = false;
      }}
      @error=${() => {
        args.component.loadingPlaceholderLoaded = false;
        args.component.loadingPlaceholderLoadingError = true;
      }}
    />
  `;
};

const renderImage = (args: LazyImageTemplateArgs) => {
  return html`
    <img
      draggable="false"
      class="odep-e-lazy-image__image"
      src="${args.src}"
      srcset="${ifDefined(args.srcset)}"
      alt="${ifDefined(args.alt)}"
      sizes="${ifDefined(args.sizes)}"
    />
  `;
};

export const lazyImageTemplate = (args: LazyImageTemplateArgs) => {
  return html`
    <style>
      ${lazyImageShadowScss}
    </style>
    <div
      class="odep-e-lazy-image__image-box"
      data-aspect-ratio="${ifDefined(args.aspectRatio)}"
      ?data-image-loaded="${args.component.imageLoaded}"
    >
      ${renderPlaceholder(args)} ${renderImage(args)}
    </div>
  `;
};

export const renderLazyImage = (args: RenderLazyImageArgs) => {
  return html`
    <odep-e-lazy-image
      class="odep-e-lazy-image${renderCssClasses(args.cssClasses)}"
      src="${args.src}"
      alt=${ifDefined(args.alt)}
      srcset=${ifDefined(args.srcset)}
      sizes=${ifDefined(args.sizes)}
      loading-placeholder=${ifDefined(args.loadingPlaceholder)}
      aspect-ratio=${ifDefined(args.aspectRatio)}
    >
    </odep-e-lazy-image>
  `;
};

export interface RenderLazyImagePlaceholderArgs {
  cssClasses?: string;
  aspectRatio: string;
}

export const renderLazyImagePlaceholder = (args: RenderLazyImagePlaceholderArgs) => {
  return html`
    <div
      class="odep-e-lazy-image odep-e-lazy-image--placeholder${renderCssClasses(args.cssClasses)}"
      aspect-ratio="${args.aspectRatio}"
    >
      <div class="odep-e-lazy-image__placeholder-content">
        ${renderIcon({
          cssClasses: "odep-e-lazy-image__placeholder-icon",
          iconId: "odep-icon-placeholder",
        })}
      </div>
    </div>
  `;
};
